import { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, Building2, Check, X, Home } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { cn } from '../lib/utils';

/**
 * SearchableBrandSelect - A searchable dropdown for brand selection
 * Replaces static dropdowns with a filterable, accessible component
 */
export const SearchableBrandSelect = ({ 
  brands = [], 
  selectedBrand, 
  onSelect, 
  placeholder = "Select Brand",
  className = "",
  showIndustry = false,
  showHomeOption = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Filter brands based on search query
  const filteredBrands = brands.filter(brand => 
    brand.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    brand.industry?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    String(brand.brand_id).includes(searchQuery)
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (brand) => {
    onSelect(brand);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onSelect(null);
  };

  const handleGoHome = () => {
    onSelect(null);
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* Trigger Button */}
      <Button
        variant="outline"
        className="w-full justify-between h-10"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="searchable-brand-select"
      >
        <div className="flex items-center gap-2 truncate">
          <Building2 className="w-4 h-4 text-muted-foreground shrink-0" />
          <span className="truncate">
            {selectedBrand?.name || placeholder}
          </span>
          {selectedBrand?.industry && showIndustry && (
            <span className="text-xs text-muted-foreground">
              ({selectedBrand.industry})
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {selectedBrand && (
            <X 
              className="w-4 h-4 text-muted-foreground hover:text-foreground cursor-pointer"
              onClick={handleClear}
            />
          )}
          <ChevronDown className={cn(
            "w-4 h-4 text-muted-foreground transition-transform",
            isOpen && "rotate-180"
          )} />
        </div>
      </Button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                ref={inputRef}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search brands..."
                className="pl-8 h-9"
                data-testid="brand-search-input"
              />
            </div>
          </div>

          {/* Brand List */}
          <div className="max-h-64 overflow-y-auto">
            {/* Home Option for Super Admin */}
            {showHomeOption && !searchQuery && (
              <div
                onClick={handleGoHome}
                className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-accent transition-colors border-b border-border bg-gradient-to-r from-violet-500/10 to-transparent"
                data-testid="brand-option-home"
              >
                <Home className="w-4 h-4 text-violet-400" />
                <div className="flex flex-col">
                  <span className="font-medium text-violet-300">Home</span>
                  <span className="text-xs text-muted-foreground">
                    Go to Brand Overview
                  </span>
                </div>
              </div>
            )}
            
            {filteredBrands.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground text-sm">
                No brands found
              </div>
            ) : (
              filteredBrands.map((brand) => (
                <div
                  key={brand.brand_id}
                  onClick={() => handleSelect(brand)}
                  className={cn(
                    "flex items-center justify-between px-3 py-2.5 cursor-pointer hover:bg-accent transition-colors",
                    selectedBrand?.brand_id === brand.brand_id && "bg-accent"
                  )}
                  data-testid={`brand-option-${brand.brand_id}`}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{brand.name}</span>
                    {brand.industry && (
                      <span className="text-xs text-muted-foreground">
                        {brand.industry} • {brand.total_stores || 0} stores
                      </span>
                    )}
                  </div>
                  {selectedBrand?.brand_id === brand.brand_id && (
                    <Check className="w-4 h-4 text-primary shrink-0" />
                  )}
                </div>
              ))
            )}
          </div>

          {/* Footer with count */}
          <div className="px-3 py-2 border-t border-border bg-muted/50 text-xs text-muted-foreground">
            {filteredBrands.length} of {brands.length} brands
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableBrandSelect;
